/**
 * @fileoverview WebSocket client for Kick.com chat events
 * @version 1.0.0
 * @author Kick Flow Team
 */

import { EventEmitter } from 'events';
import { URLSearchParams } from 'url';
import {
	WebSocketConfig,
	WebSocketState,
	WebSocketEventMap,
	WebSocketStats,
	PusherEvent,
	ChatMessage,
	ChatUserEvent,
	ChatroomUpdatedEvent,
	StreamHostEvent,
	SubscriptionEvent,
	GiftSubscriptionEvent
} from '../types/websocket';
import { DebugLogger } from './logger';

// Use native WebSocket in browser or Node.js WebSocket polyfill
const WebSocketClass = typeof WebSocket !== 'undefined' 
	? WebSocket 
	: require('ws');

/**
 * Kick.com WebSocket client for real-time chat events
 * 
 * Connects to Kick.com's Pusher-based WebSocket service to receive
 * real-time chat messages and events.
 * 
 * @example Basic usage
 * ```typescript
 * const chatWS = new KickWebSocket({
 *   chatroomId: 12345,
 *   autoReconnect: true
 * });
 * 
 * chatWS.on('message', (message) => {
 *   console.log(`${message.sender.username}: ${message.content}`);
 * });
 * 
 * chatWS.on('connect', () => {
 *   console.log('Connected to chat');
 * });
 * 
 * await chatWS.connect();
 * ```
 */
export class KickWebSocket extends EventEmitter {
	private static readonly BASE_URL = 'wss://ws-us2.pusher.com/app/32cbd69e4b950bf97679';
	private static readonly PROTOCOL_VERSION = '7';
	private static readonly CLIENT_VERSION = '7.4.0';

	private readonly config: Required<WebSocketConfig>;
	private ws: any = null;
	private state: WebSocketState = WebSocketState.DISCONNECTED;
	private reconnectTimer: NodeJS.Timeout | null = null;
	private heartbeatTimer: NodeJS.Timeout | null = null;
	private stats: WebSocketStats;
	private logger: DebugLogger;

	/**
	 * Create a new WebSocket client
	 * 
	 * @param config - WebSocket configuration
	 */
	constructor(config: WebSocketConfig) {
		super();

		this.config = {
			autoReconnect: true,
			reconnectDelay: 5000,
			maxReconnectAttempts: 10,
			debug: false,
			...config
		};

		this.logger = new DebugLogger(this.config.debug);

		this.stats = {
			state: WebSocketState.DISCONNECTED,
			messagesReceived: 0,
			uptime: 0,
			reconnectAttempts: 0
		};

		// Bind methods to preserve context
		this.handleOpen = this.handleOpen.bind(this);
		this.handleMessage = this.handleMessage.bind(this);
		this.handleClose = this.handleClose.bind(this);
		this.handleError = this.handleError.bind(this);
	}

	/**
	 * Type-safe event emitter methods
	 */
	emit<K extends keyof WebSocketEventMap>(
		event: K, 
		...args: WebSocketEventMap[K]
	): boolean {
		return super.emit(event, ...args);
	}

	on<K extends keyof WebSocketEventMap>(
		event: K, 
		listener: (...args: WebSocketEventMap[K]) => void
	): this {
		return super.on(event, listener);
	}

	once<K extends keyof WebSocketEventMap>(
		event: K, 
		listener: (...args: WebSocketEventMap[K]) => void
	): this {
		return super.once(event, listener);
	}

	off<K extends keyof WebSocketEventMap>(
		event: K, 
		listener: (...args: WebSocketEventMap[K]) => void
	): this {
		return super.off(event, listener);
	}

	/**
	 * Connect to the WebSocket
	 * 
	 * @returns Promise that resolves when connected
	 */
	async connect(): Promise<void> {
		if (this.state === WebSocketState.CONNECTING || this.state === WebSocketState.CONNECTED) {
			return;
		}

		this.setState(WebSocketState.CONNECTING);

		try {
			const url = this.buildWebSocketUrl();
			this.ws = new WebSocketClass(url);

			this.ws.addEventListener('open', this.handleOpen);
			this.ws.addEventListener('message', this.handleMessage);
			this.ws.addEventListener('close', this.handleClose);
			this.ws.addEventListener('error', this.handleError);

			// Wait for connection to be established
			await new Promise<void>((resolve, reject) => {
				const timeout = setTimeout(() => {
					reject(new Error('Connection timeout'));
				}, 10000);

				this.once('connect', () => {
					clearTimeout(timeout);
					resolve();
				});

				this.once('error', (error: Error) => {
					clearTimeout(timeout);
					reject(error);
				});
			});
		} catch (error) {
			this.setState(WebSocketState.ERROR);
			throw error;
		}
	}

	/**
	 * Disconnect from the WebSocket
	 */
	disconnect(): void {
		this.config.autoReconnect = false; // Disable auto-reconnect
		this.clearTimers();

		if (this.ws) {
			this.ws.close(1000, 'Client disconnecting');
			this.ws = null;
		}

		this.setState(WebSocketState.DISCONNECTED);
	}

	/**
	 * Get current connection state
	 */
	getState(): WebSocketState {
		return this.state;
	}

	/**
	 * Get connection statistics
	 */
	getStats(): WebSocketStats {
		return {
			...this.stats,
			uptime: this.stats.connectedAt ? Date.now() - this.stats.connectedAt.getTime() : 0
		};
	}

	/**
	 * Check if WebSocket is connected
	 */
	isConnected(): boolean {
		return this.state === WebSocketState.CONNECTED && this.ws?.readyState === 1; // WebSocket.OPEN
	}

	/**
	 * Send a raw message to the WebSocket
	 * 
	 * @param message - Message to send
	 */
	send(message: string | object): void {
		if (!this.isConnected() || !this.ws) {
			throw new Error('WebSocket is not connected');
		}

		const data = typeof message === 'string' ? message : JSON.stringify(message);
		this.ws.send(data);

		this.logger.info('WebSocket sent:', data);
	}

	/**
	 * Build WebSocket URL with proper parameters
	 */
	private buildWebSocketUrl(): string {
		const params = new URLSearchParams({
			protocol: KickWebSocket.PROTOCOL_VERSION,
			client: 'js',
			version: KickWebSocket.CLIENT_VERSION,
			flash: 'false'
		});

		return `${KickWebSocket.BASE_URL}?${params.toString()}`;
	}

	/**
	 * Handle WebSocket open event
	 */
	private handleOpen(): void {
		this.setState(WebSocketState.CONNECTED);
		this.stats.connectedAt = new Date();
		this.stats.reconnectAttempts = 0;

		// Subscribe to chatroom channel
		this.subscribeToChatroom();

		// Start heartbeat
		this.startHeartbeat();

		this.emit('connect');

		this.logger.info(`WebSocket connected to chatroom ${this.config.chatroomId}`);
	}

	/**
	 * Handle WebSocket message event
	 */
	private handleMessage(event: any): void {
		try {
			const data = event.data || event;
			const message = JSON.parse(typeof data === 'string' ? data : data.toString());
			this.stats.messagesReceived++;

			this.logger.info('WebSocket received:', message);

			this.emit('raw_event', message);
			this.processEvent(message);
		} catch (error) {
			this.logger.warn('Failed to parse WebSocket message:', error);
		}
	}

	/**
	 * Handle WebSocket close event
	 */
	private handleClose(event: any): void {
		this.clearTimers();
		this.setState(WebSocketState.DISCONNECTED);

		const reason = event.reason || 'Unknown reason';
		this.emit('disconnect', reason);

		this.logger.info(`WebSocket closed: ${event.code} - ${reason}`);

		// Auto-reconnect if enabled
		if (this.config.autoReconnect && this.stats.reconnectAttempts < this.config.maxReconnectAttempts) {
			this.scheduleReconnect();
		}
	}

	/**
	 * Handle WebSocket error event
	 */
	private handleError(event: any): void {
		const error = event.error || new Error('WebSocket error');
		this.stats.lastError = error.message;
		this.setState(WebSocketState.ERROR);
		this.emit('error', error);

		this.logger.warn('WebSocket error:', error);
	}

	/**
	 * Subscribe to chatroom channel
	 */
	private subscribeToChatroom(): void {
		const subscribeEvent = {
			event: 'pusher:subscribe',
			data: {
				auth: '',
				channel: `chatrooms.${this.config.chatroomId}.v2`
			}
		};

		this.send(subscribeEvent);
	}

	/**
	 * Start heartbeat to keep connection alive
	 */
	private startHeartbeat(): void {
		this.heartbeatTimer = setInterval(() => {
			if (this.isConnected()) {
				this.send({
					event: 'pusher:ping',
					data: {}
				});
			}
		}, 30000); // Send ping every 30 seconds
	}

	/**
	 * Schedule reconnection attempt
	 */
	private scheduleReconnect(): void {
		if (this.reconnectTimer) {
			return;
		}

		this.setState(WebSocketState.RECONNECTING);
		this.stats.reconnectAttempts++;

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			this.emit('reconnect', this.stats.reconnectAttempts);

			this.logger.info(`Reconnecting... (attempt ${this.stats.reconnectAttempts})`);

			this.connect().catch(() => {
				if (this.stats.reconnectAttempts >= this.config.maxReconnectAttempts) {
					this.emit('reconnect_failed');
					this.logger.warn('Max reconnect attempts reached');
				}
			});
		}, this.config.reconnectDelay);
	}

	/**
	 * Set WebSocket state and emit state change event
	 */
	private setState(state: WebSocketState): void {
		if (this.state !== state) {
			this.state = state;
			this.stats.state = state;
			this.emit('state_change', state);
		}
	}

	/**
	 * Clear all timers
	 */
	private clearTimers(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
	}

	/**
	 * Process incoming WebSocket events
	 */
	private processEvent(event: PusherEvent): void {
		// Parse data if it's a string (Pusher sends JSON as string)
		let eventData = event.data;
		if (typeof eventData === 'string' && eventData.trim().startsWith('{')) {
			try {
				eventData = JSON.parse(eventData);
			} catch (error) {
				this.logger.warn('Failed to parse event data:', error);
				eventData = event.data;
			}
		}

		switch (event.event) {
			case 'App\\Events\\ChatMessageEvent':
				this.handleChatMessage(eventData);
				break;

			case 'App\\Events\\UserJoinedEvent':
				this.handleUserJoined(eventData);
				break;

			case 'App\\Events\\UserLeftEvent':
				this.handleUserLeft(eventData);
				break;

			case 'App\\Events\\ChatroomUpdatedEvent':
				this.handleChatroomUpdated(eventData);
				break;

			case 'App\\Events\\StreamHostEvent':
				this.handleStreamHost(eventData);
				break;

			case 'App\\Events\\SubscriptionEvent':
				this.handleSubscription(eventData);
				break;

			case 'App\\Events\\GiftSubscriptionEvent':
				this.handleGiftSubscription(eventData);
				break;

			case 'pusher:pong':
				// Heartbeat response - ignore
				break;

			case 'pusher_internal:subscription_succeeded':
				this.logger.info('Successfully subscribed to chatroom channel');
				break;

			default:
				this.logger.info(`Unhandled event: ${event.event}`, eventData);
				break;
		}
	}

	/**
	 * Handle chat message event
	 */
	private handleChatMessage(data: any): void {
		try {
			const message: ChatMessage = {
				id: data.id,
				chatroom_id: data.chatroom_id,
				content: data.content,
				type: data.type,
				created_at: data.created_at,
				sender: {
					id: data.sender.id,
					username: data.sender.username,
					slug: data.sender.slug,
					identity: data.sender.identity
				},
				metadata: data.metadata
			};

			this.emit('message', message);
		} catch (error) {
			this.logger.warn('Failed to parse chat message:', error);
		}
	}

	/**
	 * Handle user joined event
	 */
	private handleUserJoined(data: any): void {
		const user: ChatUserEvent = {
			username: data.username,
			slug: data.slug,
			id: data.id,
			chatroom_id: data.chatroom_id
		};

		this.emit('user_joined', user);
	}

	/**
	 * Handle user left event
	 */
	private handleUserLeft(data: any): void {
		const user: ChatUserEvent = {
			username: data.username,
			slug: data.slug,
			id: data.id,
			chatroom_id: data.chatroom_id
		};

		this.emit('user_left', user);
	}

	/**
	 * Handle chatroom updated event
	 */
	private handleChatroomUpdated(data: any): void {
		const chatroom: ChatroomUpdatedEvent = {
			id: data.id,
			chatter_count: data.chatter_count,
			slow_mode: data.slow_mode,
			subscribers_mode: data.subscribers_mode,
			followers_mode: data.followers_mode,
			emotes_mode: data.emotes_mode,
			message_interval: data.message_interval,
			following_min_duration: data.following_min_duration
		};

		this.emit('chatroom_updated', chatroom);
	}

	/**
	 * Handle stream host event
	 */
	private handleStreamHost(data: any): void {
		const host: StreamHostEvent = {
			host: {
				id: data.host.id,
				username: data.host.username,
				slug: data.host.slug
			},
			number_viewers: data.number_viewers
		};

		this.emit('stream_host', host);
	}

	/**
	 * Handle subscription event
	 */
	private handleSubscription(data: any): void {
		const subscription: SubscriptionEvent = {
			username: data.username,
			channel_name: data.channel_name,
			months: data.months
		};

		this.emit('subscription', subscription);
	}

	/**
	 * Handle gift subscription event
	 */
	private handleGiftSubscription(data: any): void {
		const gift: GiftSubscriptionEvent = {
			gifter_username: data.gifter_username,
			gifted_usernames: data.gifted_usernames,
			months: data.months
		};

		this.emit('gift_subscription', gift);
	}
}
